/**
 * The site's structured data, printed as the published pages print it.
 *
 * Client-safe, like `types.ts`, and for the same reason: this may import from
 * `./types` and nothing else. Reaching for `loadBlog.ts` or `sanitize.ts` from
 * here pulls DOMPurify into the browser chunk, see the note in `blog.ts`.
 */
import {
  authorCanonicalUrl,
  blogPostCanonicalUrl,
  siteAssetUrl,
  type BlogPost,
} from "./types";

/** The logo the published posts name as the publisher's; the header draws the same file. */
const PUBLISHER_LOGO = siteAssetUrl(
  "/marketing/nav/webshare-wordmark-compact.svg"
);

/**
 * A CMS value as Webflow writes it into a page's code: HTML-escaped and
 * nothing more. An apostrophe reads `&#39;` inside the JSON, and a line break
 * in a summary is printed as it is, which leaves that post's block invalid
 * JSON, as the published one is.
 */
function asWebflowWrites(value: string | null | undefined): string {
  return (value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * The post's `BlogPosting`, character for character the published template:
 * every property printed, `""` where the post has no value, and `image` from
 * the post's Featured image, which five posts set. Pictures are named where
 * this site serves them, the only difference from the published text.
 */
export function blogPostingSchema(post: BlogPost): string {
  const url = blogPostCanonicalUrl(post.slug);
  const text = asWebflowWrites;
  const author = post.author;
  return `
{
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": "${text(post.title)}",
  "description": "${text(post.summary)}",
  "url": "${url}",
  "image": "${text(post.featuredImage?.url)}",
  "datePublished": "${text(post.publishedAt)}",
  "dateModified": "${text(post.updatedAt)}",
  "author": {
    "@type": "Person",
    "name": "${text(author?.name)}",
    "url": "${authorCanonicalUrl(text(author?.slug))}",
    "image": "${text(author?.avatar?.url)}",
    "jobTitle": "${text(author?.jobTitle)}",
    "description": "${text(author?.bio)}"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Webshare",
    "logo": {
      "@type": "ImageObject",
      "url": "${PUBLISHER_LOGO}"
    }
  },
  "articleSection": "${text(post.category?.name)}",
  "inLanguage": "en",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": "${url}"
  },
  "thumbnailUrl": "${text(post.thumbnail?.url)}"
}
`;
}

/**
 * Structured data printed as the published page prints it, character for
 * character, broken or not, the HTML its FAQ answers carry included: a
 * `</strong>` does not end a script. Only what would end the element early is
 * changed, and the published text holds none of it: `</script` written
 * `<\/script` and `<!--` written `<!--`, both still what they were to a
 * JSON reader.
 */
export function jsonLdVerbatim(text: string): string {
  return text.replace(/<\/(script)/gi, "<\\/$1").replace(/<!--/g, "\\u003c!--");
}
